import { Breadcrumb } from '@/components/shared/Breadcrumb';
import { useSettings } from '@/store/selectors';
import { SidebarNav } from 'calc-sidebar';
import type { SidebarItemDef } from 'calc-sidebar';

/**
 * Reference screen: nomogram references, dosing methods, target tables (SCREENMAP §3).
 *
 * The reference material spans 6–7 peer sections (7 in educational mode), which
 * exceeds the 5-item horizontal tab cap. Per calc-sidebar's INTEGRATION.md we use
 * a vertical SidebarNav here; each section is a sidebar item whose panel stays
 * mounted (hidden via the `hidden` attribute) so scroll state is preserved across
 * navigation (UX Invariant #8). The sidebar's `section="reference"` namespace is
 * unique on the page (CalculatorScreen's TabGroup uses `section="calc"`).
 */
export function ReferenceScreen(): JSX.Element {
  const settings = useSettings();
  const educational = settings.educationalMode;

  const items: SidebarItemDef[] = [
    {
      id: 'hartford',
      label: 'Hartford Nomogram',
      content: (
        <section className="panel" aria-label="Hartford nomogram interval determination">
          <h2 className="panel-title">Hartford Nomogram — interval determination</h2>
          <div className="table-scroll">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Random level at 6–14 h</th>
                  <th>Interval</th>
                </tr>
              </thead>
              <tbody>
                <tr><td>Below q24/q36 line</td><td>q24h</td></tr>
                <tr><td>Between q24/q36 and q36/q48</td><td>q36h</td></tr>
                <tr><td>Between q36/q48 and q48/exit</td><td>q48h</td></tr>
                <tr><td>Above q48/exit line</td><td>Exit EI → traditional</td></tr>
              </tbody>
            </table>
          </div>
          <p className="hint-text">Nicolau DP et al. Experience with a once-daily aminoglycoside program administered to 2,184 adult patients. Antimicrob Agents Chemother. 1995;39(3):650-655.</p>
        </section>
      ),
    },
    {
      id: 'methods',
      label: 'Dosing Methods',
      content: (
        <section className="panel" aria-label="Dosing methods">
          <h2 className="panel-title">Dosing methods</h2>
          <h3 className="panel-subtitle">Extended-interval dosing (Hartford)</h3>
          <p>
            Extended-interval dosing administers a large dose (5&ndash;7 mg/kg gentamicin/tobramycin)
            at an interval determined by the patient&apos;s renal function and drug clearance.
            The Hartford nomogram uses a single random level drawn 6&ndash;14 hours after the first
            dose to adjust the interval to q24h, q36h, q48h, or exit to traditional dosing.
          </p>
          <h3 className="panel-subtitle">Traditional dosing</h3>
          <p>
            Traditional dosing uses 1–1.7 mg/kg every 8–12 hours with peak targets of 5–8 mg/L
            and trough targets below 2 mg/L. Peak and trough levels are drawn at steady state
            (typically around the 3rd dose).
          </p>
          <h3 className="panel-subtitle">Sawchuk-Zaske method</h3>
          <p>
            When patient-specific PK is needed (e.g., burns, cystic fibrosis, augmented renal
            clearance), the Sawchuk-Zaske method uses three post-dose levels to calculate
            individual kₑl and Vd:
          </p>
          <pre className="eq-block">
{`kₑl = −slope of ln(concentration) vs time
Vd = dose / (C₀ + kₑl × C₀ × T_inf)
        where C₀ = back-extrapolated peak at infusion end`}
          </pre>
        </section>
      ),
    },
    {
      id: 'weight-rules',
      label: 'Weight & Contraindications',
      content: (
        <section className="panel" aria-label="Dosing weight rules and EI contraindications">
          <h2 className="panel-title">Dosing weight rules &amp; EI contraindications</h2>
          <h3 className="panel-subtitle">Dosing weight rules</h3>
          <div className="table-scroll">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Agent</th>
                  <th>Strategy</th>
                  <th>Peak (mg/L)</th>
                  <th>Trough (mg/L)</th>
                </tr>
              </thead>
              <tbody>
                <tr><td>Gentamicin/Tobramycin</td><td>Extended-interval</td><td>16–24</td><td>&lt;1</td></tr>
                <tr><td>Gentamicin/Tobramycin</td><td>Traditional</td><td>5–8</td><td>&lt;2</td></tr>
                <tr><td>Amikacin</td><td>Extended-interval</td><td>48–72</td><td>&lt;4</td></tr>
                <tr><td>Amikacin</td><td>Traditional</td><td>15–24</td><td>&lt;8</td></tr>
                <tr><td>Gentamicin</td><td>Synergy (endocarditis)</td><td>3–4</td><td>&lt;1</td></tr>
              </tbody>
            </table>
          </div>
          <ul className="bullet-list">
            <li><strong>ABW &lt; IBW:</strong> Use ABW.</li>
            <li><strong>ABW within IBW to 120% IBW:</strong> Use IBW.</li>
            <li><strong>ABW &gt; 120% IBW:</strong> Use AdjBW = IBW + 0.4 × (ABW − IBW).</li>
            <li><strong>Manual override:</strong> Clinician-specified weight with documented reason.</li>
          </ul>
          <h3 className="panel-subtitle">EI contraindications</h3>
          <ul className="bullet-list">
            <li>CrCl &lt; 20 mL/min (institutional cutoff adjustable)</li>
            <li>Unstable renal function (ΔSCr &gt; 0.3 mg/dL in 24h)</li>
            <li>Dialysis (use dedicated dialysis pathway)</li>
            <li>Pregnancy (use traditional)</li>
            <li>Ascites / burns &gt; 20% BSA (use Sawchuk-Zaske)</li>
            <li>Endocarditis synergy (use traditional)</li>
            <li>Age &gt; 80 with high toxicity risk</li>
          </ul>
        </section>
      ),
    },
    {
      id: 'quick-start',
      label: 'Quick Start',
      content: (
        <section className="panel" aria-label="About and quick start">
          <h2 className="panel-title">About / Quick start</h2>
          <h3 className="panel-subtitle">Quick start</h3>
          <ol className="bullet-list">
            <li>Enter patient demographics and labs in the Calculator tab.</li>
            <li>Select the agent and indication.</li>
            <li>Choose a dosing strategy (extended-interval is default for eligible patients).</li>
            <li>Review the derived values (dosing weight, CrCl, EI eligibility).</li>
            <li>Check the safety banner for contraindications.</li>
            <li>Use the regimen recommendation, adjusting for clinical context.</li>
            <li>If using Hartford nomogram: draw a random level 6–14 h after the first dose and enter it in the Levels tab.</li>
            <li>Follow the monitoring schedule for SCr/BUN and subsequent levels.</li>
          </ol>
          <h3 className="panel-subtitle">Safety & contraindications</h3>
          <p>
            Extended-interval dosing is contraindicated in patients with CrCl &lt; 20 mL/min,
            unstable renal function, pregnancy, ascites, burns &gt; 20% BSA, and endocarditis
            synergy. Contraindications may be overridden with a documented clinical reason;
            all overrides are logged for audit.
          </p>
          <h3 className="panel-subtitle">Data & privacy</h3>
          <p>
            All computations run locally in the browser. Patient data entered into this tool
            is stored on the device only (IndexedDB). No data is transmitted to any server.
            Clearing browser data will permanently delete all stored sessions.
          </p>
        </section>
      ),
    },
    {
      id: 'population-kel',
      label: 'Population kₑl',
      content: (
        <section className="panel" aria-label="Population kel estimation">
          <h2 className="panel-title">Population kₑl estimation</h2>
          <pre className="eq-block">
{`kₑl = 0.0024 × CrCl + 0.01
t½ = ln(2) / kₑl
Vd = 0.26 L/kg (population default)`}
          </pre>
          <p className="hint-text">
            <small><cite>Matzke GR et al. Pharmacotherapy. 2009;29(6):658-665.</cite></small>
          </p>
        </section>
      ),
    },
    {
      id: 'citations',
      label: 'Citations',
      content: (
        <section className="panel" aria-label="Citations and evidence">
          <h2 className="panel-title">Citations &amp; evidence</h2>
          <h3 className="panel-subtitle">Cockcroft-Gault equation</h3>
          <p className="hint-text">
            Cockcroft DW, Gault MH. Prediction of creatinine clearance from serum creatinine. <em>Nephron.</em> 1976;16(1):31-41.{' '}
            <a href="https://doi.org/10.1159/000180580" target="_blank" rel="noopener noreferrer">DOI: 10.1159/000180580</a>
          </p>

          <h3 className="panel-subtitle">Devine ideal body weight</h3>
          <p className="hint-text">
            Devine B. Gentamicin therapy. <em>Drug Intell Clin Pharm.</em> 1974;8:650-655.{' '}
            <a href="https://doi.org/10.1177/106002807400801104" target="_blank" rel="noopener noreferrer">DOI: 10.1177/106002807400801104</a>
          </p>

          <h3 className="panel-subtitle">Hartford nomogram</h3>
          <p className="hint-text">
            Nicolau DP, Freeman CD, Belliveau PP, et al. Experience with a once-daily aminoglycoside program administered to 2,184 adult patients. <em>Antimicrob Agents Chemother.</em> 1995;39(3):650-655.{' '}
            <a href="https://doi.org/10.1128/AAC.39.3.650" target="_blank" rel="noopener noreferrer">DOI: 10.1128/AAC.39.3.650</a>
          </p>

          <h3 className="panel-subtitle">Sawchuk-Zaske method</h3>
          <p className="hint-text">
            Sawchuk RJ, Zaske DE, Cipolle RJ, et al. Kinetic model for gentamicin dosing with the use of individual patient parameters. <em>Clin Pharmacol Ther.</em> 1977;21(3):362-369.{' '}
            <a href="https://doi.org/10.1002/cpt1977213362" target="_blank" rel="noopener noreferrer">DOI: 10.1002/cpt1977213362</a>
          </p>

          <h3 className="panel-subtitle">CKD-EPI 2021 equation</h3>
          <p className="hint-text">
            Inker LA, Eneanya ND, Coresh J, et al. New creatinine- and cystatin C-based equations to estimate GFR without race. <em>N Engl J Med.</em> 2021;385(20):1900-1901.{' '}
            <a href="https://doi.org/10.1056/NEJMc2110294" target="_blank" rel="noopener noreferrer">DOI: 10.1056/NEJMc2110294</a>
          </p>

          <h3 className="panel-subtitle">Bayesian dose individualization</h3>
          <p className="hint-text">
            Sheiner LB, Beal S, Rosenberg B, Marathe VV. Forecasting individual pharmacokinetics. <em>Clin Pharmacol Ther.</em> 1979;26(3):294-305.{' '}
            <a href="https://doi.org/10.1002/cpt1979263294" target="_blank" rel="noopener noreferrer">DOI: 10.1002/cpt1979263294</a>
          </p>
        </section>
      ),
    },
  ];

  if (educational) {
    items.push({
      id: 'educational',
      label: 'Educational Notes',
      content: (
        <section className="panel" aria-label="Educational notes">
          <h2 className="panel-title">Educational notes</h2>
          <h3 className="panel-subtitle">Why aminoglycoside dosing matters</h3>
          <p>
            Aminoglycosides (gentamicin, tobramycin, amikacin) are concentration-dependent antibiotics
            with a narrow therapeutic window. Underdosing risks treatment failure; overdosing risks
            nephrotoxicity and ototoxicity. Extended-interval dosing maximizes the peak/MIC ratio
            (Cmax/MIC &ge; 8-10) while allowing the drug to clear below a safety threshold before the
            next dose, exploiting the post-antibiotic effect.
          </p>
          <h3 className="panel-subtitle">Why Cockcroft-Gault (not CKD-EPI)?</h3>
          <p>
            Most drug dosing literature was validated against Cockcroft-Gault estimated CrCl because
            it uses actual body weight. CKD-EPI estimates GFR indexed to body surface area and is
            better for staging chronic kidney disease. When the two diverge significantly
            (|CrCl &minus; eGFR| / max &gt; 30%), clinical judgment is needed to select the more
            appropriate estimate for dosing.
          </p>
          <h3 className="panel-subtitle">When to use Sawchuk-Zaske</h3>
          <p>
            Population PK parameters may not apply in cystic fibrosis (larger Vd, faster clearance),
            burns, augmented renal clearance, or critical illness. The Sawchuk-Zaske method
            individualizes kₑl and Vd from two or three post-dose drug levels, improving accuracy
            for these special populations.
          </p>
        </section>
      ),
    });
  }

  return (
    <div className="screen" data-testid="reference-screen">
      <Breadcrumb />
      <h1 className="screen-title">Clinical Reference</h1>
      <SidebarNav
        section="reference"
        defaultItem="hartford"
        stickyTop="calc(var(--amg-header-height) + var(--amg-space-4))"
        ariaLabel="Clinical reference sections"
        items={items}
      />
    </div>
  );
}
